import { useMemo, useState } from 'react';

import type { Week } from '@/lib/types';
import { getWeekFullLabel } from '@/lib/utils/week';

/**
 * 주차 고르기 상자. 주간업무 네 화면과 식당안내가 같은 것을 쓴다.
 *
 * <h2>최신이 위, 기본은 최근 것만</h2>
 * 목록은 최신순으로 들어온다(`useWeeks`). 주차는 <b>지우지 않으므로</b> 해가 지날수록 쌓인다 —
 * 한 해에 쉰두 줄이다. 전부 펼쳐 두면 늘 쓰는 이번 주·지난 주가 긴 목록 맨 위 한 점이 되고,
 * 마우스 휠이 필요해진다. 그래서 기본은 {@link RECENT_COUNT} 개다.
 *
 * <b>과거를 못 보게 막는 것이 아니다.</b> 맨 아래 "지난 주차 모두 보기" 를 고르면 그 자리에서
 * 전부 펼쳐진다 — 다른 화면으로 가거나 날짜를 입력할 필요가 없다. 지금 고른 주차가 최근 열 개
 * 밖이면(예전 회의록을 열어 둔 경우) 그 줄만 따로 남겨 둔다. 고른 값이 목록에 없으면
 * `select` 가 빈칸으로 보이기 때문이다.
 */
const RECENT_COUNT = 10;

/** 실제 주차 id 와 섞이지 않는 값. `select` 의 value 는 문자열뿐이라 한 칸을 빌려 쓴다. */
const SHOW_ALL = '__all__';

export function WeekSelect({
  weeks,
  weekId,
  onChange,
  label = getWeekFullLabel,
}: {
  weeks: Week[];
  weekId: string | null;
  onChange: (weekId: string) => void;
  /** 줄에 적을 문구. 식당안내는 기간 없이 짧게 쓴다. */
  label?: (week: Week) => string;
}) {
  const [showAll, setShowAll] = useState(false);

  const visible = useMemo(() => {
    if (showAll) return weeks;

    const recent = weeks.slice(0, RECENT_COUNT);
    const selected = weeks.find((week) => week.id === weekId);

    // 고른 주차가 최근 열 개 밖이면 그 줄만 데려온다. 순서는 그대로 최신순이다.
    if (selected && !recent.includes(selected)) return [...recent, selected];
    return recent;
  }, [weeks, weekId, showAll]);

  if (weeks.length === 0) return null;

  const hidden = weeks.length - visible.length;

  return (
    <select
      className="input-field no-print"
      // 좁은 화면에서는 220px 을 고집하지 않는다 — 제목 옆에 서면 제목이 한두 글자로 뭉개진다.
      // 넓은 화면에서 55vw 는 늘 220px 보다 커서 아무 영향이 없다.
      style={{ width: 'auto', minWidth: 'min(220px, 55vw)' }}
      value={weekId ?? ''}
      onChange={(event) => {
        if (event.target.value === SHOW_ALL) {
          setShowAll(true);
          return; // 고른 주차는 그대로다. 목록만 길어진다.
        }
        onChange(event.target.value);
      }}
      aria-label="주차 선택"
    >
      {visible.map((week) => (
        <option key={week.id} value={week.id}>
          {label(week)}
        </option>
      ))}
      {hidden > 0 && <option value={SHOW_ALL}>지난 주차 모두 보기 ({hidden}개)</option>}
    </select>
  );
}
